import { Box } from '@mui/material';
import { useEffect } from 'react';

import LayoutWithSidebar from '../components/common/layoutWithSidebar';
import NoDataFallback from '../components/common/NoDataFallback';
import ServerLogsData from '../components/ServerLogs/ServerLogsData';
import ServerLogsSkeleton from '../components/ServerLogs/ServerLogsSkeleton';
import useServerLogsData from '../hooks/useServerLogsData';

interface ServerLogsProps {
  callSid: string;
  agentId: string;
  searchTerm: string;
}

const ServerLogs = ({ callSid, agentId, searchTerm }: ServerLogsProps) => {
  const {
    fetchLogData: fetchLogDataFromHook,
    parsedLogs,
    isLoading,
    error,
  } = useServerLogsData(callSid, agentId);

  useEffect(() => {
    if (agentId && callSid) {
      fetchLogDataFromHook();
    }
  }, [agentId, callSid, fetchLogDataFromHook]);

  // Show error if agentId is missing
  if (!agentId) {
    return (
      <LayoutWithSidebar margin={2}>
        <NoDataFallback
          message="Agent ID Required"
          description="Please provide agentId in the URL parameters to view server logs."
        />
      </LayoutWithSidebar>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 2,
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
      }}
    >
      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Show skeleton when loading */}
        {isLoading && <ServerLogsSkeleton />}

        {/* Show error only when not loading and there's an error */}
        {!isLoading && error && (
          <NoDataFallback
            message="Failed to Load Server Logs"
            description={'No server logs found for this call'}
          />
        )}

        {/* Show data only when not loading, no error, and has data */}
        {!isLoading && !error && (
          <ServerLogsData parsedLogs={parsedLogs} searchTerm={searchTerm} />
        )}
      </Box>
    </Box>
  );
};

export default ServerLogs;
